import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { categories, getFeaturedComics, getLatestUpdates, getHotComics } from '@/mocks/comics';

export default function Home() {
  const { t } = useTranslation();
  const featured = getFeaturedComics().slice(0, 6);
  const latest = getLatestUpdates().slice(0, 8);
  const hot = getHotComics().slice(0, 10);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '奇妙漫画',
    url: 'https://www.qimiaomanhua.com',
    description: t('seo.homeDesc'),
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.qimiaomanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title={t('seo.homeTitle')}
        description={t('seo.homeDesc')}
        canonicalUrl="https://www.qimiaomanhua.com"
        schema={pageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-labelledby="hero-heading">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20comic%20book%20illustration%20background%20with%20warm%20orange%20coral%20and%20cream%20tones%2C%20dynamic%20speech%20bubbles%20and%20manga%20speed%20lines%2C%20soft%20gradient%20with%20floating%20ink%20splashes%2C%20modern%20minimalist%20composition%2C%20clean%20and%20vibrant%20design%20for%20comic%20website%20hero&width=1920&height=1080&seq=hero-bg-2026&orientation=landscape"
            alt="奇妙漫画 - 免费漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/25 to-foreground-950/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 max-w-3xl w-full animate-fade-in-up">
              <h1 id="hero-heading" className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4">
                {t('home.heroTitle')}
              </h1>
              <p className="text-lg md:text-xl text-background-100/90 mb-3">
                {t('home.heroSubtitle')}
              </p>
              <p className="text-sm md:text-base text-background-200/80 mb-8 max-w-2xl mx-auto">
                {t('home.heroDesc')}
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/category/hanman"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('home.startReading')}
                </Link>
                <Link
                  to="#categories"
                  className="px-8 py-3 border-2 border-background-50/60 text-background-50 rounded-full font-medium text-sm hover:bg-background-50 hover:text-foreground-900 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('home.browseCategory')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Comics */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="featured-heading">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 id="featured-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {t('home.featuredTitle')}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">{t('home.featuredDesc')}</p>
            </div>
            <Link to="/category/hanman" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              {t('home.viewAll')} <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {featured.map((comic, idx) => (
              <div key={comic.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.08}s` }}>
                <ComicCard comic={comic} />
              </div>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section id="categories" className="bg-background-100 py-12 md:py-16" aria-labelledby="categories-heading">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 id="categories-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {t('home.categoryTitle')}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">{t('home.categoryDesc')}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
              {categories.map((cat, idx) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 bg-background-50 rounded-lg hover:bg-primary-50 transition-all duration-300 hover:scale-105 cursor-pointer"
                  style={{ animationDelay: `${idx * 0.06}s` }}
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16" aria-labelledby="latest-heading">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 id="latest-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {t('home.latestTitle')}
              </h2>
              <p className="text-sm text-foreground-500 mt-2">{t('home.latestDesc')}</p>
            </div>
            <Link to="/category/hanman" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              {t('home.viewAll')} <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {latest.map((comic, idx) => (
              <div key={comic.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.06}s` }}>
                <ComicCard comic={comic} />
              </div>
            ))}
          </div>
        </section>

        {/* Hot Ranking */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-14 md:pb-18" aria-labelledby="hot-heading">
          <div className="mb-8">
            <h2 id="hot-heading" className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {t('home.hotTitle')}
            </h2>
            <p className="text-sm text-foreground-500 mt-2">{t('home.hotDesc')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-5">
            {hot.map((comic, idx) => (
              <div key={comic.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                <ComicCard comic={comic} showRank={idx + 1} />
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}